import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import axios from 'axios';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { TTSVoice } from '../types';
import { serializeError } from '../utils/apiUtils';

// Using original Azure configuration with environment variable fallback
const AZURE_CONFIG = {
  key: '9eiqNFW2EGxZN09M6HktdPIt8Ks0UfuidOJYm9IA5VFgEaj8pTVzJQQJ99AKACYeBjFXJ3w3AAAYACOGlq0e',
  region: 'eastus'
};

const GOOGLE_API_ENDPOINT = 'https://texttospeech.googleapis.com/v1';
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_CLOUD_API_KEY;

const DAILY_PREVIEW_LIMIT = 10;
const PREMIUM_PREVIEW_LIMIT = 50;
const MAX_PREVIEW_LENGTH = 200;

export const getVoicePreview = async (
  voice: TTSVoice,
  userId?: string | null
): Promise<{ url: string; error?: string }> => {
  try {
    if (userId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();

      if (!userData) {
        return {
          error: 'User profile not found',
          url: ''
        };
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      
      const dailyPreviews = userData.previewsToday?.timestamp === today
        ? userData.previewsToday.count
        : 0;

      const previewLimit = userData.subscription === 'premium' 
        ? PREMIUM_PREVIEW_LIMIT 
        : DAILY_PREVIEW_LIMIT;

      if (dailyPreviews >= previewLimit) {
        return {
          error: `Daily preview limit reached (${dailyPreviews}/${previewLimit})`,
          url: ''
        };
      }

      await updateDoc(doc(db, 'users', userId), {
        previewsToday: {
          timestamp: today,
          count: increment(1)
        }
      });
    }

    const previewText = voice.previewText.slice(0, MAX_PREVIEW_LENGTH);

    if (voice.provider === 'azure') {
      return new Promise((resolve) => {
        try {
          const speechConfig = sdk.SpeechConfig.fromSubscription(AZURE_CONFIG.key, AZURE_CONFIG.region);
          
          // Set high-quality audio output format - match the audio generation quality
          speechConfig.speechSynthesisOutputFormat = sdk.SpeechSynthesisOutputFormat.Audio48Khz192KBitRateMonoMp3;
          speechConfig.speechSynthesisVoiceName = voice.id;

          // Enable word boundary events for better timing
          speechConfig.setProperty("WordBoundary", "1");
          
          // Create a custom AudioOutputStream that doesn't play audio directly
          // This matches the approach used in audio generation
          const audioOutputStream = sdk.AudioOutputStream.createPullStream();
          const audioConfig = sdk.AudioConfig.fromStreamOutput(audioOutputStream);
          
          // Create synthesizer with custom audio config to prevent browser-specific playback issues
          const synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioConfig);

          const ssml = `
<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="${voice.language}">
  <voice name="${voice.id}">
    <prosody rate="0%" pitch="0Hz">
      ${voice.style ? `<mstts:express-as style="${voice.style}">` : ''}
      ${previewText}
      ${voice.style ? '</mstts:express-as>' : ''}
    </prosody>
  </voice>
</speak>`.trim();

          synthesizer.speakSsmlAsync(
            ssml,
            result => {
              const { audioData } = result;
              synthesizer.close();

              if (!audioData || audioData.byteLength === 0) {
                resolve({
                  error: 'No audio data received',
                  url: ''
                });
                return;
              }

              try {
                const audioArray = new Uint8Array(audioData);
                // Process audio data in chunks to avoid "Maximum call stack size exceeded" error
                let binary = '';
                const chunkSize = 1024; // Process in smaller chunks to avoid call stack issues
                
                // Process the array in chunks to avoid the "too many arguments" error
                for (let i = 0; i < audioArray.length; i += chunkSize) {
                  const chunk = audioArray.slice(i, i + chunkSize);
                  // Convert Uint8Array to regular array for String.fromCharCode
                  const chunkArray = Array.from(chunk);
                  binary += String.fromCharCode.apply(null, chunkArray);
                }
                
                const base64String = btoa(binary);
                resolve({
                  url: `data:audio/mp3;base64,${base64String}`
                });
              } catch (error: any) {
                resolve({
                  error: 'Failed to process audio data: ' + error.message,
                  url: ''
                });
              }
            },
            error => {
              synthesizer.close();
              resolve({
                error: 'Speech synthesis failed: ' + error,
                url: ''
              });
            }
          );
        } catch (error: any) {
          resolve({
            error: 'Failed to initialize speech synthesis: ' + error.message,
            url: ''
          });
        }
      });
    } else if (voice.provider === 'google') {
      try {
        if (!GOOGLE_API_KEY) {
          return {
            error: 'Google Cloud API key not configured',
            url: ''
          };
        }

        // Log voice details for debugging
        console.log('Preview request for Google voice:', {
          id: voice.id,
          name: voice.name,
          type: voice.type,
          language: voice.language
        });

        // Note: Google now uses both traditional IDs (en-US-Standard-A) and star names (Achernar, Achird, etc.)
        // Both are valid, so we don't need strict validation here

        // Build audioConfig - Standard voices don't support pitch/speakingRate
        const audioConfig: any = {
          audioEncoding: 'MP3',
          volumeGainDb: 0
        };

        // Only add pitch and speakingRate for voices that support them (not Standard voices)
        if (voice.supportsSpeed) {
          audioConfig.speakingRate = 1;
        }
        if (voice.supportsPitch) {
          audioConfig.pitch = 0;
        }

        const response = await axios.post(
          `${GOOGLE_API_ENDPOINT}/text:synthesize`,
          {
            input: {
              text: previewText
            },
            voice: {
              languageCode: voice.language,
              name: voice.id
            },
            audioConfig
          },
          {
            params: { key: GOOGLE_API_KEY },
            timeout: 15000
          }
        );

        if (!response.data?.audioContent) {
          return {
            error: 'No audio content received from Google API',
            url: ''
          };
        }

        return {
          url: `data:audio/mp3;base64,${response.data.audioContent}`
        };
      } catch (error: any) {
        console.error('Google preview generation failed:', serializeError(error));
        console.error('Voice details:', { id: voice.id, type: voice.type, language: voice.language });
        console.error('Full error response:', error.response?.data);
        return {
          error: error.response?.data?.error?.message || error.message || 'Failed to generate preview',
          url: ''
        };
      }
    } else {
      return {
        error: 'Unsupported voice provider',
        url: ''
      };
    }
  } catch (error: any) {
    console.error('Preview generation failed:', {
      error: serializeError(error),
      voiceId: voice.id,
      provider: voice.provider
    });
    
    return {
      error: error.message || 'Failed to generate preview',
      url: ''
    };
  }
};